import { hasPayerAngleBrackets, normalizePayerFreeText, payerTextPattern, payerTextMessage, preventUnsafePayerPaste } from "./payerTextValidation";
import type { ClipboardEvent } from "react";
import { Alert, Button, Checkbox, Col, Divider, Form, Input, Row, Select, Space } from "antd";
import Card from "antd/es/card/Card";
import { useEffect, useMemo, useRef, useState } from "react";
import { LoaderSpinner } from "../../../components/common/Loader";
import { getCobPayerChangeRequests, saveAndApproveCobPayerChangeRequest, saveCobPayer, saveCobPayerAddressOtherInformation, submitCobPayerChangeRequest } from "../../../service/httpsCalls";
import "../billing.scss";
import { ChangeRequestApprovalStatus, CobInvoiceType, CobPayerPatterns, CobPayerValidation } from "./cobPayerConstants";

interface LookupItem {
    id: number;
    name: string;
}

interface LookupWithClientItem extends LookupItem {
    clientId: number;
}

interface ClearingHouseLookupItem {
    selectionValue: number;
    id: number | null;
    name: string;
    clientId: number;
    isDefault: boolean;
}

export interface CobPayerAuditHistory {
    id: number;
    action?: string;
    changedBy?: string;
    changedDate?: string;
    approvedBy?: string;
    approvalDate?: string;
    fieldName?: string;
    oldValue?: string;
    newValue?: string;
}

export interface CobPayerDetail {
    id: number;
    clientId: number;
    code: string;
    name: string;
    clearingHouseId: number | null;
    isActive: boolean;
    invoiceTypeId: number;
    cobPdrCaseId?: number;
    clearingHousePayerId?: string;
    clearingHousePayerName?: string;
    payerDetail?: string;
    payerAddrLine1?: string;
    payerAddrLine2?: string;
    payerCityName?: string;
    payerStateCode?: string;
    payerZipCode?: string;
    payerContactPhone?: string;
    note?: string;
}

interface CreateCobPayerProps {
    clientList: LookupItem[];
    clearingHouseList: ClearingHouseLookupItem[];
    invoiceTypeList: LookupWithClientItem[];
    pdrCaseList: LookupWithClientItem[];
    stateList: LookupItem[];
    payer?: CobPayerDetail;
    canEdit?: boolean;
    onSaved: (payer: CobPayerSavedContext) => void;
    onCancel: () => void;
    canDelete?: boolean;
    canApproveReject?: boolean;
    deleting?: boolean;
    onDelete?: () => void;
    notify: (type: "success" | "error" | "warning", message: string) => void;
}

export interface CobPayerSavedContext {
    id: number;
    clientId: number;
    invoiceTypeId: number;
    isEdit: boolean;
}

interface CobPayerFormValues {
    clientId: number;
    code: string;
    name: string;
    clearingHouseSelectionValue?: number;
    isActive: boolean;
    invoiceTypeId: number;
    cobPdrCaseId?: number;
    clearingHousePayerId?: string;
    clearingHousePayerName?: string;
    payerDetail?: string;
    payerAddrLine1?: string;
    payerAddrLine2?: string;
    payerCityName?: string;
    payerStateCode?: string;
    payerZipCode?: string;
    payerContactPhone?: string;
    note?: string;
    changeReason?: string;
}

export default function CreateCobPayer({
    clientList,
    clearingHouseList,
    invoiceTypeList,
    pdrCaseList,
    stateList,
    payer,
    canEdit = true,
    onSaved,
    onCancel,
    canDelete = false,
    canApproveReject = false,
    deleting = false,
    onDelete,
    notify
}: CreateCobPayerProps) {
    const [form] = Form.useForm<CobPayerFormValues>();
    const [saving, setSaving] = useState(false);
    const [changeRequestAction, setChangeRequestAction] = useState<"submit" | "approve" | null>(null);
    const [savingAddress, setSavingAddress] = useState(false);
    const [hasApprovalControlledChanges, setHasApprovalControlledChanges] = useState(false);
    const [hasPendingChangeRequest, setHasPendingChangeRequest] = useState(false);
    const [checkingPendingChangeRequest, setCheckingPendingChangeRequest] = useState(false);
    const isEdit = Boolean(payer);
    const initialEditClientHandled = useRef(false);
    const formValues = (Form.useWatch(values => values, form) ?? {}) as CobPayerFormValues;
    const selectedClientId = formValues.clientId;
    const invoiceTypeId = formValues.invoiceTypeId;
    const clearingHouseSelectionValue = formValues.clearingHouseSelectionValue;
    const clientClearingHouseList = useMemo(
        () => clearingHouseList.filter(item => item.clientId === selectedClientId),
        [clearingHouseList, selectedClientId]
    );
    const defaultClearingHouse = useMemo(
        () => clientClearingHouseList.find(item => item.isDefault),
        [clientClearingHouseList]
    );
    const selectedClearingHouse = useMemo(
        () => clientClearingHouseList.find(item => item.selectionValue === clearingHouseSelectionValue),
        [clearingHouseSelectionValue, clientClearingHouseList]
    );
    const clientInvoiceTypeList = useMemo(
        () => invoiceTypeList.filter(item => item.clientId === selectedClientId),
        [invoiceTypeList, selectedClientId]
    );
    const clientPdrCaseList = useMemo(
        () => pdrCaseList.filter(item => item.clientId === selectedClientId),
        [pdrCaseList, selectedClientId]
    );
    const isPdrCase = invoiceTypeId === CobInvoiceType.PdrCases;
    const is837p = invoiceTypeId === CobInvoiceType._837p;
    const requiresClearingHousePayer = is837p && selectedClearingHouse?.id != null;
    const changeRequestDisabled = isEdit && hasPendingChangeRequest;
    const normalizeText = (value?: string | null) => value?.trim() || null;
    const normalizeId = (value?: number | null) => value == null ? null : Number(value);
    const hasControlledChanges = (values: CobPayerFormValues) => {
        if (!payer) return false;

        const currentInvoiceTypeId = normalizeId(values.invoiceTypeId);
        const currentIs837p = currentInvoiceTypeId === CobInvoiceType._837p;
        const currentIsPdrCase = currentInvoiceTypeId === CobInvoiceType.PdrCases;
        const currentClearingHouse = clientClearingHouseList.find(
            item => item.selectionValue === values.clearingHouseSelectionValue);

        return normalizeText(values.name) !== normalizeText(payer.name) ||
            currentInvoiceTypeId !== normalizeId(payer.invoiceTypeId) ||
            normalizeId(currentIs837p ? currentClearingHouse?.id : null) !==
                normalizeId(payer.clearingHouseId) ||
            normalizeText(currentIs837p ? values.clearingHousePayerId : null) !==
                normalizeText(payer.clearingHousePayerId) ||
            normalizeText(currentIs837p ? values.clearingHousePayerName : null) !==
                normalizeText(payer.clearingHousePayerName) ||
            normalizeId(currentIsPdrCase ? values.cobPdrCaseId : null) !==
                normalizeId(payer.cobPdrCaseId);
    };

    const checkPendingChangeRequest = () => new Promise<boolean>((resolve) => {
        if (!payer) {
            resolve(false);
            return;
        }

        setCheckingPendingChangeRequest(true);
        getCobPayerChangeRequests(payer.id, (result: any) => {
            setCheckingPendingChangeRequest(false);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to verify pending change requests.");
                resolve(true);
                return;
            }

            const pendingExists = (result.data ?? []).some((request: any) =>
                request.approvalStatus === ChangeRequestApprovalStatus[ChangeRequestApprovalStatus.Pending]);
            setHasPendingChangeRequest(pendingExists);
            resolve(pendingExists);
        });
    });

    useEffect(() => {
        if (payer) {
            checkPendingChangeRequest();
        }
    }, [payer?.id]);

    const invoiceTypeChanged = (value: number) => {
        form.setFieldsValue({
            clearingHouseSelectionValue: value === CobInvoiceType._837p ? defaultClearingHouse?.selectionValue : undefined,
            cobPdrCaseId: undefined,
            clearingHousePayerId: undefined,
            clearingHousePayerName: undefined
        });
        form.setFields([
            { name: "clearingHouseSelectionValue", errors: [] },
            { name: "cobPdrCaseId", errors: [] },
            { name: "clearingHousePayerId", errors: [] },
            { name: "clearingHousePayerName", errors: [] }
        ]);
    };

    const clearingHouseChanged = () => {
        form.setFieldsValue({
            clearingHousePayerId: undefined,
            clearingHousePayerName: undefined
        });
        form.setFields([
            { name: "clearingHousePayerId", errors: [] },
            { name: "clearingHousePayerName", errors: [] }
        ]);
    };

    useEffect(() => {
        if (!selectedClientId) {
            return;
        }

        if (isEdit && !initialEditClientHandled.current && selectedClientId === payer?.clientId) {
            initialEditClientHandled.current = true;
            return;
        }

        form.setFieldsValue({
            clearingHouseSelectionValue: defaultClearingHouse?.selectionValue,
            invoiceTypeId: CobInvoiceType._837p,
            cobPdrCaseId: undefined,
            clearingHousePayerId: undefined,
            clearingHousePayerName: undefined
        });
    }, [defaultClearingHouse?.selectionValue, form, isEdit, payer?.clientId, selectedClientId]);
    const submit = (values: CobPayerFormValues) => {
        const { clearingHouseSelectionValue, ...payerValues } = values;
        const clearingHouse = clientClearingHouseList.find(item => item.selectionValue === clearingHouseSelectionValue);
        const hasClearingHouse = is837p && clearingHouse?.id != null;
        const payload = {
            ...payerValues,
            clearingHouseId: is837p ? (clearingHouse?.id ?? null) : null,
            code: values.code.trim().toUpperCase(),
            name: values.name.trim(),
            isPdrCase,
            cobPdrCaseId: isPdrCase ? values.cobPdrCaseId : null,
            clearingHousePayerId: hasClearingHouse ? values.clearingHousePayerId?.trim() : null,
            clearingHousePayerName: hasClearingHouse ? values.clearingHousePayerName?.trim() : null
        };
        const save = (callback: (result: any) => void) => saveCobPayer(payload, callback);

        setSaving(true);
        save((result: any) => {
            setSaving(false);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to save COB Payer.");
                return;
            }

            notify("success", "COB Payer added successfully.");
            onSaved({
                id: result.id ?? payer?.id,
                clientId: values.clientId,
                invoiceTypeId: values.invoiceTypeId,
                isEdit
            });
        });
    };

    const buildChangeRequestPayload = (values: CobPayerFormValues) => {
        const clearingHouse = clientClearingHouseList.find(
            item => item.selectionValue === values.clearingHouseSelectionValue);
        const is837pInvoice = values.invoiceTypeId === CobInvoiceType._837p;
        return {
            name: values.name.trim(),
            invoiceTypeId: values.invoiceTypeId,
            clearingHouseId: values.invoiceTypeId === CobInvoiceType._837p ? (clearingHouse?.id ?? null) : null,
            clearingHousePayerId: is837pInvoice ? values.clearingHousePayerId?.trim() : null,
            clearingHousePayerName: is837pInvoice ? values.clearingHousePayerName?.trim() : null,
            cobPdrCaseId: values.invoiceTypeId === CobInvoiceType.PdrCases ? values.cobPdrCaseId : null,
            changeReason: values.changeReason?.trim()
        };
    };

    const normalizePayerField = (maxLength: number) => (value: string, previousValue = "") => {
        const nextValue = normalizePayerFreeText(value, previousValue, maxLength);
        if (hasPayerAngleBrackets(value) && nextValue === previousValue && value !== previousValue) {
            notify("warning", payerTextMessage);
        }
        return nextValue;
    };
    const payerInputProps = {
        onPaste: (event: ClipboardEvent<HTMLElement>) => preventUnsafePayerPaste(event, () => notify("warning", payerTextMessage))
    };

    const validateFormFields = async (fieldNames: (keyof CobPayerFormValues)[]) => {
        try {
            return await form.validateFields(fieldNames);
        } catch {
            notify("warning", "Please fill required details");
            return null;
        }
    };

    const saveChangeRequest = async (approveImmediately: boolean) => {
        setChangeRequestAction(approveImmediately ? "approve" : "submit");
        const pendingExists = await checkPendingChangeRequest();
        if (pendingExists) {
            setChangeRequestAction(null);
            notify("warning", "A change request is already pending approval for this COB Payer.");
            return;
        }

        const fieldNames: (keyof CobPayerFormValues)[] = ["name", "invoiceTypeId", "changeReason"];
        if (isPdrCase) fieldNames.push("cobPdrCaseId");
        if (is837p) fieldNames.push("clearingHouseSelectionValue", "clearingHousePayerId", "clearingHousePayerName");
        const values = await validateFormFields(fieldNames);
        if (!values) {
            setChangeRequestAction(null);
            return;
        }
        const save = approveImmediately ? saveAndApproveCobPayerChangeRequest : submitCobPayerChangeRequest;
        save(payer!.id, buildChangeRequestPayload({ ...form.getFieldsValue(), ...values }), (result: any) => {
            setChangeRequestAction(null);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to save change request.");
                return;
            }
            notify("success", approveImmediately ? "Changes saved and approved successfully." : "Change request submitted successfully.");
            onSaved({ id: payer!.id, clientId: payer!.clientId, invoiceTypeId: values.invoiceTypeId, isEdit: true });
        });
    };

    const saveAddressOtherInformation = async () => {
        const fields: (keyof CobPayerFormValues)[] = ["payerDetail", "payerAddrLine1", "payerAddrLine2",
            "payerCityName", "payerStateCode", "payerZipCode", "payerContactPhone", "isActive", "note"];
        const values = await validateFormFields(fields);
        if (!values) return;
        setSavingAddress(true);
        saveCobPayerAddressOtherInformation(payer!.id, values, (result: any) => {
            setSavingAddress(false);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to save address and other information.");
                return;
            }
            notify("success", "Address and other information saved successfully.");
            onSaved({ id: payer!.id, clientId: payer!.clientId, invoiceTypeId: payer!.invoiceTypeId, isEdit: true });
        });
    };
    return (
        <div style={{ paddingTop: "12px" }}>
            <Form<CobPayerFormValues>
                form={form}
                disabled={isEdit && !canEdit}
                className="cob-payer-form"
                layout="vertical"
                requiredMark={false}
                initialValues={{
                    ...payer,
                    clientId: payer?.clientId ?? clientList[0]?.id,
                    clearingHouseSelectionValue: payer
                        ? clearingHouseList.find(item => item.clientId === payer.clientId && item.id === payer.clearingHouseId)?.selectionValue
                        : undefined,
                    invoiceTypeId: payer?.invoiceTypeId ?? CobInvoiceType._837p,
                    isActive: payer?.isActive ?? true
                }}
                onFinish={isEdit ? () => undefined : submit}
                onValuesChange={(_, values) =>
                    setHasApprovalControlledChanges(hasControlledChanges(values))}
                onFinishFailed={() => {
                    notify("warning", "Please fill required details");
                }}
                style={{ background: "transparent" }}
            >
                {isEdit && checkingPendingChangeRequest && (
                    <div style={{ padding: "8px 0 12px" }}>
                        <LoaderSpinner size={24} />
                    </div>
                )}
                {isEdit && !checkingPendingChangeRequest && hasPendingChangeRequest && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: "12px" }}
                        message="Change request pending approval"
                        description="A change request is already pending for this COB Payer. Approve, reject, or cancel it before submitting another change request. Address and Other Information can still be saved directly."
                    />
                )}
                <Card styles={{ body: { padding: "15px" } }}>
                    <p
                        style={{
                            fontSize: 14,
                            color: "rgba(0, 0, 0, 0.85)",
                            marginTop: 0,
                            fontWeight: 500
                        }}
                    >
                        {isEdit ? "Change Request" : "COB Payer Information"}
                    </p>
            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Form.Item name="clientId" label="Client" rules={[{ required: true }]}>
                        <Select
                            placeholder="Select Client"
                            disabled={isEdit}
                            options={clientList.map(item => ({ value: item.id, label: item.name }))}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        name="code"
                        label="Payer Code"
                        normalize={(value: string) => value?.toUpperCase().replace(CobPayerPatterns.RemoveNonAlphanumeric, "")}
                        rules={[
                            { required: true },
                            { max: CobPayerValidation.PayerCodeMaxLength },
                            { pattern: CobPayerPatterns.PayerCode }
                        ]}
                    >
                        <Input maxLength={CobPayerValidation.PayerCodeMaxLength} placeholder="Enter Payer Code" disabled={isEdit} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        name="name"
                        label="Payer Name"
                        normalize={normalizePayerField(CobPayerValidation.PayerNameMaxLength)}
                        rules={[
                            { required: true, whitespace: true },
                            { max: CobPayerValidation.PayerNameMaxLength },
                            { pattern: payerTextPattern, message: payerTextMessage }
                        ]}
                    >
                        <Input {...payerInputProps} disabled={changeRequestDisabled} maxLength={CobPayerValidation.PayerNameMaxLength} placeholder="Enter Payer Name" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name="invoiceTypeId" label="Invoice Submission Type" rules={[{ required: true }]}>
                        <Select
                            disabled={changeRequestDisabled}
                            placeholder="Select Invoice Submission Type"
                            options={clientInvoiceTypeList.map(item => ({ value: item.id, label: item.name }))}
                            onChange={invoiceTypeChanged}
                        />
                    </Form.Item>
                </Col>
                {isPdrCase && (
                    <Col xs={24} md={8}>
                        <Form.Item name="cobPdrCaseId" label="PDR Case" rules={[{ required: true }]}>
                            <Select disabled={changeRequestDisabled} showSearch optionFilterProp="label" placeholder="Select PDR Case" options={clientPdrCaseList.map(item => ({ value: item.id, label: item.name }))} />
                        </Form.Item>
                    </Col>
                )}
                {is837p && (
                    <>
                        <Col xs={24} md={8}>
                            <Form.Item name="clearingHouseSelectionValue" label="Clearing House" rules={[{ required: true }]}>
                                <Select
                                    disabled={changeRequestDisabled}
                                    placeholder="Select Clearing House"
                                    onChange={clearingHouseChanged}
                                    options={clientClearingHouseList.map(item => ({
                                        value: item.selectionValue,
                                        label: item.name
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="clearingHousePayerId"
                                label="Clearing House Payer ID"
                                normalize={(value: string) => value?.toUpperCase().replace(CobPayerPatterns.RemoveNonAlphanumeric, "")}
                                rules={[{ required: requiresClearingHousePayer, whitespace: true }, { max: CobPayerValidation.PayerCodeMaxLength }]}
                            >
                                <Input disabled={changeRequestDisabled} maxLength={CobPayerValidation.PayerCodeMaxLength} placeholder="Enter Clearing House Payer ID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="clearingHousePayerName"
                                label="Clearing House Payer Name"
                                normalize={normalizePayerField(CobPayerValidation.PayerNameMaxLength)}
                                rules={[{ required: requiresClearingHousePayer, whitespace: true }, { max: CobPayerValidation.PayerNameMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}
                            >
                                <Input {...payerInputProps} disabled={changeRequestDisabled} maxLength={CobPayerValidation.PayerNameMaxLength} placeholder="Enter Clearing House Payer Name" />
                            </Form.Item>
                        </Col>
                    </>
                )}
                {isEdit && (
                    <Col xs={24}>
                        <Form.Item name="changeReason" label="Reason for Change" normalize={normalizePayerField(CobPayerValidation.ChangeReasonMaxLength)} style={{ marginBottom: 32 }} rules={[
                            { required: true, whitespace: true, message: "Reason for Change is required." },
                            { max: CobPayerValidation.ChangeReasonMaxLength },
                            { pattern: payerTextPattern, message: payerTextMessage }
                        ]}>
                            <Input {...payerInputProps} disabled={changeRequestDisabled} showCount maxLength={CobPayerValidation.ChangeReasonMaxLength} placeholder="Enter reason for the requested changes" />
                        </Form.Item>
                    </Col>
                )}
                    </Row>
                    {isEdit && canEdit && (
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button type="primary" loading={changeRequestAction === "submit"} disabled={changeRequestDisabled || !hasApprovalControlledChanges || changeRequestAction !== null} onClick={() => saveChangeRequest(false)}>
                                Submit Change Request
                            </Button>
                            {canApproveReject && (
                                <Button loading={changeRequestAction === "approve"} disabled={changeRequestDisabled || !hasApprovalControlledChanges || changeRequestAction !== null} onClick={() => saveChangeRequest(true)}>
                                    Save and Approve
                                </Button>
                            )}
                        </Space>
                    )}
                </Card>

                <Card styles={{ body: { padding: "15px" } }} style={{ marginTop: "12px" }}>
                    <p
                        style={{
                            fontSize: 14,
                            color: "rgba(0, 0, 0, 0.85)",
                            marginTop: 0,
                            fontWeight: 500
                        }}
                    >
                        Address and Contact
                    </p>
                    <Row gutter={16}>
                <Col xs={24} md={8}><Form.Item name="payerDetail" label="Payer Detail" normalize={normalizePayerField(CobPayerValidation.PayerDetailMaxLength)} rules={[{ max: CobPayerValidation.PayerDetailMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}><Input {...payerInputProps} maxLength={CobPayerValidation.PayerDetailMaxLength} placeholder="Enter Payer Detail" /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="payerAddrLine1" label="Address Line 1" normalize={normalizePayerField(CobPayerValidation.AddressLineMaxLength)} rules={[{ max: CobPayerValidation.AddressLineMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}><Input {...payerInputProps} maxLength={CobPayerValidation.AddressLineMaxLength} placeholder="Enter Address Line 1" /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="payerAddrLine2" label="Address Line 2" normalize={normalizePayerField(CobPayerValidation.AddressLineMaxLength)} rules={[{ max: CobPayerValidation.AddressLineMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}><Input {...payerInputProps} maxLength={CobPayerValidation.AddressLineMaxLength} placeholder="Enter Address Line 2" /></Form.Item></Col>
                <Col xs={24} md={8}><Form.Item name="payerCityName" label="City" normalize={normalizePayerField(CobPayerValidation.CityMaxLength)} rules={[{ max: CobPayerValidation.CityMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}><Input {...payerInputProps} maxLength={CobPayerValidation.CityMaxLength} placeholder="Enter City" /></Form.Item></Col>
                <Col xs={24} md={8}>
                    <Form.Item name="payerStateCode" label="State" rules={[{ max: CobPayerValidation.StateCodeMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select State"
                            options={stateList.map(item => ({ value: item.name, label: item.name }))}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        name="payerZipCode"
                        label="ZIP Code"
                        normalize={(value: string) => value?.replace(CobPayerPatterns.RemoveNonNumeric, "").slice(0, CobPayerValidation.ZipCodeMaxLength)}
                        rules={[{ max: CobPayerValidation.ZipCodeMaxLength }, { pattern: CobPayerPatterns.Numeric }]}
                    >
                        <Input inputMode="numeric" maxLength={CobPayerValidation.ZipCodeMaxLength} placeholder="Enter ZIP Code" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        name="payerContactPhone"
                        label="Contact Phone"
                        normalize={(value: string) => value?.replace(CobPayerPatterns.RemoveNonNumeric, "").slice(0, CobPayerValidation.ContactPhoneMaxLength)}
                        rules={[{ max: CobPayerValidation.ContactPhoneMaxLength }, { pattern: CobPayerPatterns.Numeric }]}
                    >
                        <Input inputMode="numeric" maxLength={CobPayerValidation.ContactPhoneMaxLength} placeholder="Enter Contact Phone" />
                    </Form.Item>
                </Col>
                    </Row>
                </Card>

                <Card styles={{ body: { padding: "15px" } }} style={{ marginTop: "12px" }}>
                    <p
                        style={{
                            fontSize: 14,
                            color: "rgba(0, 0, 0, 0.85)",
                            marginTop: 0,
                            fontWeight: 500
                        }}
                    >
                        Additional Information
                    </p>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="isActive"
                                valuePropName="checked"
                                style={{ marginBottom: 8 }}
                            >
                                <Checkbox>Active</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={24}><Form.Item name="note" label="Note" normalize={normalizePayerField(CobPayerValidation.NoteMaxLength)} rules={[{ max: CobPayerValidation.NoteMaxLength }, { pattern: payerTextPattern, message: payerTextMessage }]}><Input.TextArea {...payerInputProps} maxLength={CobPayerValidation.NoteMaxLength} showCount rows={2} placeholder="Enter Note" /></Form.Item></Col>
                    </Row>

                    <Divider style={{ margin: "10px 0" }} />
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                {isEdit && canEdit && (
                    <Button type="primary" loading={savingAddress} onClick={saveAddressOtherInformation}>
                        Save Address & Other Information
                    </Button>
                )}
                {isEdit && canDelete && (
                    <Button
                        danger
                        onClick={onDelete}
                        loading={deleting}
                        disabled={saving || hasPendingChangeRequest}
                    >
                        Delete
                    </Button>
                )}
                <Button
                    type="primary"
                    onClick={onCancel}
                    disabled={canEdit ? saving || deleting : false}
                    style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }}
                >
                    Cancel
                </Button>
                {!isEdit && <Button
                    htmlType="submit"
                    loading={saving}
                    disabled={deleting}
                    style={{ backgroundColor: "#ff4d4f", color: "#fff" }}
                >
                    Save
                </Button>}
                    </Space>
                </Card>
            </Form>
        </div>
    );
}
